<template>
    <Header class="register-header" :step="currentStep" :maxStepToDisplay="lastStep - 1" @goBack="goBack"/>
    <div ref="languageSwitchAnchor" class="language-switch-anchor"></div>
    <Loader v-if="isLoadingRegistrationFormOptions"></Loader>
    <div v-else-if="errorLoadingRegistrationFormOptions" class="container register-error-message is-flex is-justify-content-center is-align-items-center">
        Es ist ein Fehler aufgetreten, bitte versuche es später erneut.
    </div>
    <div v-else class="register container is-fluid">
        <SelectDestination v-if="showSelectDestination" @destinationSelected="setDestination"/>
        <InputData v-if="showInputData"
                   :destination="destination"
                   :registrationFormOptions="registrationFormOptions"
                   :registrationData="registrationData"
                   @dataCollected="setRegistrationData"
        />
        <ConfirmData v-if="showConfirmData"
                     :registrationData="registrationData"
                     :destination="destination"
                     :registrationFormOptions="registrationFormOptions"
                     :confirm-button-enabled="!isSendingRegistrationData"
                     @dataConfirmed="sendRegistrationData"
        />
        <DisplayMessage v-if="showDisplayMessage" :success="registrationRequestSuccess" @tryAgain="goBackToConfirmData"/>
    </div>
</template>

<script>
import Header from '@/views/components/ui/Register/Header'
import SelectDestination from "@/views/components/ui/Register/Step0SelectDestination";
import InputData from "@/views/components/ui/Register/Step1InputData";
import ConfirmData from "@/views/components/ui/Register/Step2ConfirmData";
import DisplayMessage from "@/views/components/ui/Register/Step3DisplayMessage";
import Loader from '@/views/components/ui/Loader';

import { Destination } from '@/utils/registration'
import { trackMetaPixelEvent } from '@/utils/metaPixel'


export default {
    name: 'Register',
    components: {
        Header,
        SelectDestination,
        InputData,
        ConfirmData,
        DisplayMessage,
        Loader,
    },
    props: ['registrationConfigurationId'],
    data() {
        return {
            destination: '',
            registrationFormOptions: null,
            isLoadingRegistrationFormOptions: false,
            errorLoadingRegistrationFormOptions: false,
            currentStep: 1,
            lastStep: this.registrationConfigurationId ? 3 : 4,
            registrationData: null,
            registrationRequestSuccess: null,
            isSendingRegistrationData: null,
            languageSwitchOriginalParent: null,
            languageSwitchOriginalNextSibling: null,
        }
    },
    computed: {
        showSelectDestination() {
            return (this.lastStep - this.currentStep) === 3
        },
        showInputData() {
            return (this.lastStep - this.currentStep) === 2
        },
        showConfirmData() {
            return (this.lastStep - this.currentStep) === 1
        },
        showDisplayMessage() {
            return this.currentStep === this.lastStep
        },
    },
    methods: {
        loadDataFromSession() {
            const serializedRegistrationSessionData = sessionStorage.getItem('registrationSessionData')
            if (null === serializedRegistrationSessionData) {
                return
            }

            const registrationSessionData = JSON.parse(serializedRegistrationSessionData)
            if (registrationSessionData.registrationConfigurationId !== this.registrationConfigurationId) {
                return
            }

            // reset all values when loading values for last step from session
            if (registrationSessionData.currentStep === this.lastStep) {
                this.destination = ''
                this.registrationData = null
                this.currentStep = 1
                this.registrationRequestSuccess = null
                this.registrationFormOptions = null
                return
            }

            this.destination = registrationSessionData.destination
            this.registrationData = registrationSessionData.registrationData
            this.currentStep = registrationSessionData.currentStep
            this.registrationRequestSuccess = registrationSessionData.registrationRequestSuccess
            this.registrationFormOptions = registrationSessionData.registrationFormOptions
        },
        saveDataToSession() {
            const registrationSessionData = {
                registrationConfigurationId: this.registrationConfigurationId,
                destination: this.destination,
                registrationData: this.registrationData,
                currentStep: this.currentStep,
                registrationRequestSuccess: this.registrationRequestSuccess,
                registrationFormOptions: this.registrationFormOptions
            }
            sessionStorage.setItem('registrationSessionData', JSON.stringify(registrationSessionData))
        },
        async loadRegistrationFormOptionsForConfiguration(registrationConfigurationId) {
            this.isLoadingRegistrationFormOptions = true
            this.errorLoadingRegistrationFormOptions = false

            try {
                const registrationFormOptions = await this.$store.dispatch(
                    'loadRegistrationFormOptions',
                    {registrationConfigurationId: registrationConfigurationId},
                )
                const registrationFormType = registrationFormOptions.type
                switch (registrationFormType) {
                    case 'company':
                        this.destination = Destination.Work
                        this.registrationFormOptions = registrationFormOptions
                        break
                    case 'school':
                        this.destination = Destination.School
                        this.registrationFormOptions = registrationFormOptions
                        break
                    case 'kita':
                        this.destination = Destination.Kita
                        this.registrationFormOptions = registrationFormOptions
                        break
                    default:
                        console.error('Unknown registration form type:', registrationFormType)
                        this.errorLoadingRegistrationFormOptions = true
                }

                this.isLoadingRegistrationFormOptions = false
            } catch(error) {
                console.error('Error when fetching registration form options from api: ', error)
                this.errorLoadingRegistrationFormOptions = true
                this.isLoadingRegistrationFormOptions = false
            }
        },
        async loadRegistrationFormOptionsForDestination(destination) {
            this.isLoadingRegistrationFormOptions = true
            this.errorLoadingRegistrationFormOptions = false

            try {
                this.registrationFormOptions = await this.$store.dispatch(
                    'loadRegistrationFormOptions',
                    {destination: destination},
                )

                this.isLoadingRegistrationFormOptions = false
            } catch(error) {
                console.error('Error when fetching registration form options from api: ', error)
                this.errorLoadingRegistrationFormOptions = true
                this.isLoadingRegistrationFormOptions = false
            }
        },
        async setDestination(destination) {
            if (destination === Destination.Home || destination === Destination.Work) {
                await this.loadRegistrationFormOptionsForDestination(destination)
                this.destination = destination
                this.currentStep++
            }
        },
        setRegistrationData(registrationData) {
            this.registrationData = registrationData
            if (this.currentStep < this.lastStep) {
                this.currentStep++
            }
        },
        async sendRegistrationData() {
            this.isSendingRegistrationData = true
            try {
                const requestPayload = {
                    registrationData: this.registrationData,
                    registrationConfigurationId: this.registrationConfigurationId,
                    destination: this.destination,
                }
                await this.$store.dispatch('sendRegistrationRequest', requestPayload)
                this.registrationData = null
                this.registrationRequestSuccess = true
                trackMetaPixelEvent('CompleteRegistration')
                this.currentStep++
                this.isSendingRegistrationData = false
            } catch (error) {
                console.error('Error when sending registration request: ', error)
                this.registrationRequestSuccess = false
                this.currentStep++
                this.isSendingRegistrationData = false
            }
        },
        goBackToConfirmData() {
            this.currentStep = this.lastStep - 1
            this.registrationRequestSuccess = null
        },
        goBack() {
            if (this.currentStep <= 1) {
                this.$router.push({name: 'Home'})
                return
            }

            this.currentStep--
        },
        moveLanguageSwitchIntoRegister() {
            const anchor = this.$refs.languageSwitchAnchor
            if (!anchor) {
                return
            }

            const languageSwitch = document.querySelector('.language-switch-wrapper')
            if (!languageSwitch || anchor.contains(languageSwitch)) {
                return
            }

            this.languageSwitchOriginalParent = languageSwitch.parentNode
            this.languageSwitchOriginalNextSibling = languageSwitch.nextSibling

            anchor.appendChild(languageSwitch)
            languageSwitch.classList.add('language-switch--register')
        },
        resetLanguageSwitchPosition() {
            const languageSwitch = document.querySelector('.language-switch-wrapper')
            if (!languageSwitch || !this.languageSwitchOriginalParent) {
                return
            }

            languageSwitch.classList.remove('language-switch--register')

            if (this.languageSwitchOriginalNextSibling && this.languageSwitchOriginalParent.contains(this.languageSwitchOriginalNextSibling)) {
                this.languageSwitchOriginalParent.insertBefore(languageSwitch, this.languageSwitchOriginalNextSibling)
            } else {
                this.languageSwitchOriginalParent.appendChild(languageSwitch)
            }

            this.languageSwitchOriginalParent = null
            this.languageSwitchOriginalNextSibling = null
        }
    },
    async beforeMount() {
        this.loadDataFromSession()

        if (this.registrationConfigurationId && this.registrationFormOptions === null) {
            await this.loadRegistrationFormOptionsForConfiguration(this.registrationConfigurationId)
        }
    },
    mounted() {
        this.$nextTick(() => {
            this.moveLanguageSwitchIntoRegister()
        })
    },
    beforeUnmount() {
        this.resetLanguageSwitchPosition()
    },
    watch: {
        currentStep() {
            this.saveDataToSession()
        },
        registrationData() {
            this.saveDataToSession()
        },
        registrationRequestSuccess() {
            this.saveDataToSession()
        },
        registrationFormOptions() {
            this.saveDataToSession()
        },
        destination() {
            this.saveDataToSession()
        }
    }
}
</script>

<style lang="scss">
.register {
    padding-top: 3.5rem;

    h1 {
        font-size: $fontsize-headline6;
        font-weight: $font-weight-bold;
        padding: 1rem 0;
    }

    p.subheadline {
        font-size: $font-size-medium;
        font-weight: $font-weight-bold;
        padding: 1rem 0;
    }

    p {
        padding-bottom: 1rem;
    }

    .subnote-send {
        padding: 1rem 0;
    }

    label.small {
        font-size: 11px;
        color: $dark-grey;
    }

    input {
        margin-bottom: .5rem;
    }

    label a {
        font-weight: $font-weight-black;
    }

    span {
        display: inline
    }

    .subnote {
        font-size: $font-size-xxsmall;
    }
}

.register-header {
    width: 100%;
    height: 102px;
}

.language-switch-anchor {
    position: relative;
    width: 100%;
    height: 0;
}

.container.register-error-message {
    height: 50vh;
    color: $warning-color;
}
</style>
